import { Select } from "../select";
import { useEffect, useRef, useState } from "react";
import { FileText, FolderGit2, HelpCircle, Save, Video } from "lucide-react";
import { apiFetch, uploadResource } from "../lib/auth";
import {
  CreateModal,
  MarkdownField,
  inputClass,
  labelClass,
} from "../admin-ui";
import { VideoField } from "./video-field";
import { QuizBuilder } from "./quiz-builder";
import type { Lesson, LessonKind, Mutate } from "./types";

export const lessonKindLabels: Record<LessonKind, string> = {
  video: "Video",
  article: "Artículo",
  quiz: "Quiz",
  project: "Proyecto",
};

export const lessonKindIcons: Record<
  LessonKind,
  typeof Video | typeof FileText
> = {
  video: Video,
  article: FileText,
  quiz: HelpCircle,
  project: FolderGit2,
};

/**
 * Right-hand pane: everything about one lesson.
 *
 * Mount with `key={lesson.id}` — the form is uncontrolled, so a new lesson
 * needs a fresh instance for `defaultValue` to take effect.
 */
export function LessonEditor({
  lesson,
  courseId,
  mutate,
}: {
  lesson: Lesson;
  courseId: string;
  mutate: Mutate;
}) {
  const [videoUrl, setVideoUrl] = useState(lesson.videoUrl ?? "");
  const [kind, setKind] = useState<LessonKind>(lesson.kind);
  return (
    <div className="border-ink bg-bone shadow-brutal border-surface">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const data = new FormData(event.currentTarget);
          mutate(
            () =>
              apiFetch(`/api/admin/lessons/${lesson.id}`, {
                method: "PATCH",
                body: JSON.stringify({
                  title: data.get("title"),
                  content: data.get("content"),
                  durationMinutes: Number(data.get("durationMinutes")),
                  published: data.get("published") === "on",
                  kind,
                  transcript: data.get("transcript"),
                  captionsUrl: data.get("captionsUrl") || null,
                  thumbnailUrl: data.get("thumbnailUrl") || null,
                  preview: data.get("preview") === "on",
                  videoUrl,
                  // A Mux lesson keeps its playback id in videoUrl; deriving the
                  // source from the string alone would misread it as "external"
                  // and break Mux playback, so preserve "mux" while the video is
                  // untouched. The server guards this too.
                  videoSource:
                    lesson.videoSource === "mux" && videoUrl === lesson.videoUrl
                      ? "mux"
                      : videoUrl.includes("vimeo.com")
                        ? "vimeo"
                        : videoUrl
                          ? "external"
                          : null,
                }),
              }),
            "Clase guardada.",
            courseId,
          );
        }}
        className="grid gap-3 p-4 sm:grid-cols-2"
      >
        <label className={labelClass}>
          Título
          <input
            name="title"
            defaultValue={lesson.title}
            required
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          Tipo de clase
          <Select
            name="kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as LessonKind)}
            className={inputClass}
          >
            {Object.entries(lessonKindLabels).map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </Select>
        </label>
        <label className={labelClass}>
          Duración en minutos
          <input
            name="durationMinutes"
            type="number"
            min="0"
            defaultValue={lesson.durationMinutes}
            className={inputClass}
          />
        </label>

        {kind === "video" && (
          <VideoField
            lessonId={lesson.id}
            url={videoUrl}
            onChange={setVideoUrl}
          />
        )}

        <MarkdownField
          name="content"
          label="Contenido de la clase (Markdown)"
          defaultValue={lesson.content}
        />
        <MarkdownField
          name="transcript"
          label="Transcripción"
          defaultValue={lesson.transcript}
          rows={10}
        />
        <label className={`${labelClass} sm:col-span-2`}>
          Subtítulos WebVTT (URL)
          <input
            name="captionsUrl"
            type="url"
            defaultValue={lesson.captionsUrl ?? ""}
            className={inputClass}
          />
        </label>
        <label className={`${labelClass} sm:col-span-2`}>
          Miniatura de la clase (URL de imagen, 16:9)
          <input
            name="thumbnailUrl"
            type="url"
            defaultValue={lesson.thumbnailUrl ?? ""}
            className={inputClass}
          />
        </label>
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input
            name="preview"
            type="checkbox"
            defaultChecked={lesson.preview}
          />{" "}
          Vista previa pública
        </label>
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input
            name="published"
            type="checkbox"
            defaultChecked={lesson.published}
          />{" "}
          Publicada
        </label>
        <div className="flex justify-end sm:col-span-2">
          <button className="border-ink bg-ink font-display text-paper border-chip flex items-center gap-1 px-3 py-2 text-xs font-bold">
            <Save size={14} /> Guardar clase
          </button>
        </div>
      </form>
      {/*
        Kind-specific block. Article and video content already live in the form
        above; quiz and project each get their own editor so an author only
        sees what the chosen kind actually needs.
      */}
      {kind === "quiz" && (
        <div className="border-ink bg-bone border-t-(length:--border-chip)">
          <QuizBuilder
            lessonId={lesson.id}
            courseId={courseId}
            mutate={mutate}
          />
        </div>
      )}
      <LessonEnhancements
        lesson={lesson}
        courseId={courseId}
        mutate={mutate}
        kind={kind}
      />
    </div>
  );
}

type LessonResource = {
  id: string;
  title: string;
  url: string | null;
  content: string | null;
  kind: string;
  downloadable: boolean;
  position: number;
};

type ResourceBody = {
  title: string;
  url: string | null;
  content: string | null;
  kind: string;
  downloadable: boolean;
  position: number;
};

const uploadErrorLabel = (code: string) =>
  code === "file_too_large"
    ? "El archivo supera los 25 MB."
    : code === "unsupported_format"
      ? "Ese tipo de archivo no está permitido."
      : "No se pudo subir el archivo. Prueba de nuevo.";

// The resource form adapts to the selected "Tipo": Enlace shows a URL field;
// Archivo/Plantilla add an attach-and-upload button that fills the URL; Código
// swaps the URL for a snippet textarea stored inline (no file, no link).
function ResourceForm({
  editing,
  lessonId,
  onSubmit,
}: {
  editing: LessonResource | null;
  lessonId: string;
  onSubmit: (body: ResourceBody) => Promise<void>;
}) {
  const [title, setTitle] = useState(editing?.title ?? "");
  const [kind, setKind] = useState(editing?.kind ?? "link");
  const [url, setUrl] = useState(editing?.url ?? "");
  const [content, setContent] = useState(editing?.content ?? "");
  const [downloadable, setDownloadable] = useState(
    editing?.downloadable ?? false,
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedName, setUploadedName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showsUpload = kind === "file" || kind === "template";
  const showsUrl = kind === "link" || showsUpload;
  const showsCode = kind === "code";

  return (
    <form
      className="mt-4 grid gap-3 sm:grid-cols-2"
      onSubmit={async (event) => {
        event.preventDefault();
        if (uploading) return;
        await onSubmit({
          title: title.trim(),
          kind,
          url: showsUrl ? url.trim() || null : null,
          content: showsCode ? content : null,
          downloadable: showsUrl ? downloadable : false,
          position: editing?.position ?? 0,
        });
      }}
    >
      <label className={labelClass}>
        Nombre
        <input
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          className={inputClass}
        />
      </label>
      <label className={labelClass}>
        Tipo
        <Select
          value={kind}
          onChange={(event) => setKind(event.target.value)}
          className={inputClass}
        >
          <option value="link">Enlace</option>
          <option value="file">Archivo</option>
          <option value="code">Código</option>
          <option value="template">Plantilla</option>
        </Select>
      </label>
      {showsUpload && (
        <div className="sm:col-span-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              setUploading(true);
              setUploadError(null);
              try {
                const uploadedUrl = await uploadResource(file, lessonId);
                setUrl(uploadedUrl);
                setUploadedName(file.name);
                setDownloadable(true);
              } catch (error) {
                setUploadError(
                  uploadErrorLabel(
                    error instanceof Error ? error.message : "upload_failed",
                  ),
                );
              } finally {
                setUploading(false);
              }
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="border-ink bg-lemon border-chip px-3 py-2 font-bold disabled:opacity-60"
          >
            {uploading ? "Subiendo…" : "Adjuntar y subir archivo"}
          </button>
          {uploadedName && !uploadError && (
            <p className="text-ink-soft mt-2 font-mono text-xs">
              ✓ {uploadedName}
            </p>
          )}
          {uploadError && (
            <p className="text-bubble mt-2 font-mono text-xs">{uploadError}</p>
          )}
        </div>
      )}
      {showsUrl && (
        <label className={`${labelClass} sm:col-span-2`}>
          URL
          <input
            type="url"
            required={!showsUpload}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={
              showsUpload
                ? "Se completa al subir, o pega una URL externa"
                : "https://…"
            }
            className={inputClass}
          />
        </label>
      )}
      {showsCode && (
        <label className={`${labelClass} sm:col-span-2`}>
          Código
          <textarea
            required
            rows={8}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Pega tu código aquí…"
            className={`${inputClass} font-mono text-[12px]`}
            spellCheck={false}
          />
        </label>
      )}
      {showsUrl && (
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input
            type="checkbox"
            checked={downloadable}
            onChange={(event) => setDownloadable(event.target.checked)}
          />{" "}
          Descargable
        </label>
      )}
      <button className="border-ink bg-acid border-chip px-3 py-2 font-bold sm:col-span-2">
        {editing ? "Guardar cambios" : "Agregar recurso"}
      </button>
    </form>
  );
}

function LessonEnhancements({
  lesson,
  courseId,
  mutate,
  kind,
}: {
  lesson: Lesson;
  courseId: string;
  mutate: Mutate;
  kind: LessonKind;
}) {
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [editing, setEditing] = useState<LessonResource | null>(null);
  const [resources, setResources] = useState<LessonResource[]>([]);
  const reload = () =>
    apiFetch<{ resources: LessonResource[] }>(
      `/api/admin/learning-tools/lessons/${lesson.id}/resources`,
    )
      .then((result) => setResources(result.resources))
      .catch(() => undefined);
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one load per lesson
  }, [lesson.id]);
  return (
    <div className="border-ink bg-bone border-t-(length:--border-chip) space-y-4 p-4">
      <details className="border-ink bg-paper border-chip p-3">
        <summary className="font-display cursor-pointer font-bold">
          Recursos descargables y enlaces
        </summary>
        <button
          onClick={() => {
            setEditing(null);
            setResourceModalOpen(true);
          }}
          className="border-ink bg-acid border-chip mt-4 flex items-center gap-2 px-3 py-2 font-bold"
        >
          Agregar recurso
        </button>
        {resources.length > 0 && (
          <ul className="mt-4 space-y-2">
            {resources.map((resource) => (
              <li
                key={resource.id}
                className="border-ink bg-bone border-chip flex items-center gap-3 p-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold">{resource.title}</p>
                  <p className="text-ink-soft font-mono text-xs uppercase tracking-widest">
                    {resource.kind}
                    {resource.downloadable ? " · descargable" : ""}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditing(resource);
                    setResourceModalOpen(true);
                  }}
                  className="border-ink border-chip px-2 py-1 font-mono text-xs font-bold"
                >
                  Editar
                </button>
                <button
                  onClick={async () => {
                    const saved = await mutate(
                      () =>
                        apiFetch(
                          `/api/admin/learning-tools/resources/${resource.id}`,
                          { method: "DELETE" },
                        ),
                      "Recurso borrado.",
                      courseId,
                    );
                    if (saved) void reload();
                  }}
                  className="border-ink bg-bubble border-chip px-2 py-1 font-mono text-xs font-bold"
                >
                  Borrar
                </button>
              </li>
            ))}
          </ul>
        )}
        <CreateModal
          open={resourceModalOpen}
          onClose={() => setResourceModalOpen(false)}
          title={editing ? "Editar recurso" : "Nuevo recurso"}
          description={editing ? editing.title : `Agrégalo a ${lesson.title}.`}
        >
          <ResourceForm
            key={editing?.id ?? "new"}
            editing={editing}
            lessonId={lesson.id}
            onSubmit={async (body) => {
              const saved = await mutate(
                () =>
                  editing
                    ? apiFetch(
                        `/api/admin/learning-tools/resources/${editing.id}`,
                        { method: "PATCH", body: JSON.stringify(body) },
                      )
                    : apiFetch(
                        `/api/admin/learning-tools/lessons/${lesson.id}/resources`,
                        { method: "POST", body: JSON.stringify(body) },
                      ),
                editing ? "Recurso actualizado." : "Recurso agregado.",
                courseId,
              );
              if (saved) {
                void reload();
                setResourceModalOpen(false);
                setEditing(null);
              }
            }}
          />
        </CreateModal>
      </details>
      {kind === "project" && (
        <details open className="border-ink bg-paper border-chip p-3">
          <summary className="font-display cursor-pointer font-bold">
            Proyecto y entrega
          </summary>
          <form
            className="mt-4 space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              mutate(
                () =>
                  apiFetch(
                    `/api/admin/learning-tools/lessons/${lesson.id}/project`,
                    {
                      method: "PUT",
                      body: JSON.stringify({
                        instructions: data.get("instructions"),
                        rubric: data.get("rubric"),
                        solutionUrl: data.get("solutionUrl") || null,
                        required: data.get("required") === "on",
                      }),
                    },
                  ),
                "Proyecto guardado.",
                courseId,
              );
            }}
          >
            <textarea
              name="instructions"
              required
              rows={6}
              placeholder="Consigna en Markdown"
              className={inputClass}
            />
            <textarea
              name="rubric"
              rows={5}
              placeholder="Rúbrica de evaluación"
              className={inputClass}
            />
            <input
              name="solutionUrl"
              type="url"
              placeholder="URL de solución (solo instructores)"
              className={inputClass}
            />
            <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
              <input name="required" type="checkbox" defaultChecked />{" "}
              Obligatorio para certificar
            </label>
            <button className="border-ink bg-acid border-chip w-full px-3 py-2 font-bold">
              Guardar proyecto
            </button>
          </form>
        </details>
      )}
    </div>
  );
}
